import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO_CONFIG = {
  siteName: 'AiFlo.ai',
  siteUrl: 'https://aiflo.ai',
  defaultTitle: 'AiFlo.ai - AI-First Enterprise Consulting & Integration Services',
  defaultDescription: 'Transform your enterprise with AiFlo.ai\'s AI-first consulting services. Expert in SAP, Oracle, Salesforce integration, cloud migration, and digital transformation. 150+ enterprises transformed.',
  defaultKeywords: 'AI consulting services, enterprise integration platform, digital transformation consulting, AI-first enterprise solutions, SAP implementation, Salesforce integration, cloud migration services, enterprise automation, low-code development, business process automation',
  twitterHandle: '@AiFlo_ai',
  ogImage: 'https://aiflo.ai/og-image.jpg',
};

export const PAGE_SEO = {
  '/': {
    title: 'AiFlo.ai - AI-First Enterprise Consulting & Integration Services',
    description: 'Transform your enterprise with AI-powered consulting services. Expert in SAP, Oracle, Salesforce, cloud integration, and digital transformation. Single partner, complete accountability. 150+ enterprises served.',
    keywords: 'AI consulting services, enterprise integration, digital transformation, AI-first consulting, enterprise automation, SAP implementation, Salesforce integration, cloud services',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'AiFlo.ai',
      url: 'https://aiflo.ai',
      logo: 'https://aiflo.ai/logo.png',
      description: 'AI-first enterprise consulting and software services company',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'Global'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-234-567-890',
        contactType: 'Sales',
        email: 'contact@aiflo.ai'
      },
      sameAs: [
        'https://www.linkedin.com/company/aiflo-ai',
        'https://twitter.com/AiFlo_ai'
      ]
    }
  },
  '/about': {
    title: 'About AiFlo.ai - Our Mission & Vision 2030 | Enterprise AI Consulting',
    description: 'Learn about AiFlo.ai\'s journey from startup to leading AI-first enterprise partner. Our mission: eliminate enterprise fragmentation with unified AI-powered solutions.',
    keywords: 'about AiFlo.ai, AI consulting company, enterprise transformation, company mission, vision 2030, AI-first approach',
  },
  '/services': {
    title: 'Enterprise Services - AI, Cloud, Integration & Automation | AiFlo.ai',
    description: 'Comprehensive enterprise services: AI & Automation, Cloud Infrastructure, Data Analytics, Integration, Low-Code Development, and Enterprise Applications. 40-60% faster delivery.',
    keywords: 'enterprise services, AI automation, cloud migration, data analytics, enterprise integration, low-code development, SAP services, Oracle consulting',
  },
  '/how-we-work': {
    title: 'Our Methodology - AI-Augmented Delivery Process | AiFlo.ai',
    description: 'Discover our proven 5-phase delivery methodology combining human expertise with AI acceleration. Fixed price, time & materials, and managed services options available.',
    keywords: 'consulting methodology, agile delivery, AI-augmented consulting, project delivery, engagement models, enterprise implementation',
  },
  '/industries': {
    title: 'Industry Solutions - Manufacturing, Finance, Retail & More | AiFlo.ai',
    description: 'Industry-specific enterprise solutions for Manufacturing, Financial Services, Retail, Healthcare, Logistics, and Government sectors. Proven results across 25+ countries.',
    keywords: 'industry solutions, manufacturing IT, financial services technology, retail digital transformation, healthcare IT solutions',
  },
  '/case-studies': {
    title: 'Case Studies - Real Enterprise Transformation Results | AiFlo.ai',
    description: 'Explore successful enterprise transformations: 40% cost reduction in manufacturing, 3x improved scalability in finance, 85% customer satisfaction in retail.',
    keywords: 'case studies, enterprise success stories, digital transformation examples, ROI results, client testimonials',
  },
  '/blog': {
    title: 'Blog - Enterprise AI & Technology Insights | AiFlo.ai',
    description: 'Expert insights on enterprise AI, cloud computing, integration patterns, and digital transformation. Stay ahead with latest technology trends and best practices.',
    keywords: 'enterprise technology blog, AI insights, cloud computing articles, integration best practices, digital transformation tips',
  },
  '/contact': {
    title: 'Contact Us - Get Free Enterprise Consultation | AiFlo.ai',
    description: 'Schedule a free consultation with our enterprise solution experts. Email: contact@aiflo.ai | Phone: +1-234-567-890 | Global presence across 25 countries.',
    keywords: 'contact AiFlo.ai, enterprise consultation, get quote, request demo, sales contact',
  }
};

const SEOHead = ({ title, description, keywords, path, schema }) => {
  const location = useLocation();
  const currentPath = path || location.pathname;
  const pageSEO = PAGE_SEO[currentPath] || {};
  
  const pageTitle = title || pageSEO.title || SEO_CONFIG.defaultTitle;
  const pageDescription = description || pageSEO.description || SEO_CONFIG.defaultDescription;
  const pageKeywords = keywords || pageSEO.keywords || SEO_CONFIG.defaultKeywords;
  const pageSchema = schema || pageSEO.schema;
  const canonicalUrl = `${SEO_CONFIG.siteUrl}${currentPath}`;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;
    
    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', pageDescription);
    updateMetaTag('keywords', pageKeywords);
    updateMetaTag('author', 'AiFlo.ai');
    updateMetaTag('robots', 'index, follow');
    
    // Open Graph tags
    updateMetaTag('og:title', pageTitle, true);
    updateMetaTag('og:description', pageDescription, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:site_name', SEO_CONFIG.siteName, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:image', SEO_CONFIG.ogImage, true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', pageTitle);
    updateMetaTag('twitter:description', pageDescription);
    updateMetaTag('twitter:image', SEO_CONFIG.ogImage);
    updateMetaTag('twitter:site', SEO_CONFIG.twitterHandle);
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    
    // Add structured data (JSON-LD)
    if (pageSchema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(pageSchema);
    }
  }, [pageTitle, pageDescription, pageKeywords, canonicalUrl, pageSchema]);

  return null;
};

export default SEOHead;
